/**
 * @file 右上角待办「去处理」跳转后的处理 Hook
 * @description
 * 待办跳转会带 state.fromTodo，目标页据此重设筛选并重新查询。分三种情况：
 * 1. 页面没打开过：首次挂载由页面自己用 URL 参数做 initialValues，本 hook 不处理；
 * 2. 页面已缓存在后台页签：切回来时（useActivate）处理；
 * 3. 页面就是当前页签：再跳同一地址不会触发 useActivate，靠 PendingTasks 派发的
 *    TODO_JUMP_EVENT 事件处理。
 * 每次跳转的 location.key 不同，处理过的 key 记下来，避免切页签回来重复覆盖用户改过的筛选。
 *
 * 用法：
 *   useTodoJump((params) => {
 *     formRef.current?.setFieldsValue({ status: Number(params.get('status')) });
 *     formRef.current?.submit();
 *   });
 */
import { history } from '@umijs/max';
import { useEffect, useRef } from 'react';
import { useActivate, useUnactivate } from 'react-activation';

/** PendingTasks 跳转后派发，当前正开着的目标页收到后处理 */
export const TODO_JUMP_EVENT = 'todo:jump';

export function useTodoJump(apply: (params: URLSearchParams) => void) {
  const pathRef = useRef(history.location.pathname);
  // 首次挂载那次跳转交给页面的 initialValues，不在这里重复处理
  const appliedKeyRef = useRef(history.location.key);
  const activeRef = useRef(true);
  const applyRef = useRef(apply);
  applyRef.current = apply;

  const run = (delay: number) => {
    const { key, pathname, search, state } = history.location as any;
    if (pathname !== pathRef.current || key === appliedKeyRef.current || !state?.fromTodo) return;
    appliedKeyRef.current = key;
    setTimeout(() => applyRef.current(new URLSearchParams(search)), delay);
  };

  useActivate(() => {
    activeRef.current = true;
    // 晚于 useKeepAliveSearchFix 的断连重挂和搜索值恢复，不然会被旧快照盖掉
    run(400);
  });

  useUnactivate(() => {
    activeRef.current = false;
  });

  useEffect(() => {
    // 后台页签的表单可能已断连，交给切回来时的 useActivate
    const handler = () => activeRef.current && run(0);
    window.addEventListener(TODO_JUMP_EVENT, handler);
    return () => window.removeEventListener(TODO_JUMP_EVENT, handler);
  }, []);
}
